import React, { useState, useMemo } from 'react';
import { Search, Plus, X, FileText } from 'lucide-react';
import { MaintenanceTemplate } from '../../../TemplatesEditor/types';
import { templateOfferedTo } from '../../../../../Helpers/maintenanceTemplates';

interface TemplateSearchModalProps {
    show: boolean;
    templates: MaintenanceTemplate[];
    currentComponentType: string;
    currentBuildingKey?: string;
    /** The component type's own templates that were removed from this component; listed first */
    removedTemplateIds?: string[];
    getTranslation: (key: string | undefined) => string;
    onSelectTemplate: (template: MaintenanceTemplate) => void;
    /** Receives the current search term, used as the new template's prefilled name */
    onCreateNew: (searchTerm: string) => void;
    onClose: () => void;
}

/**
 * Modal for searching and selecting maintenance templates.
 * Shows all templates with suggested templates (matching primaryComponentType) first.
 * Filters by global templates + current building templates.
 */
export const TemplateSearchModal: React.FC<TemplateSearchModalProps> = ({
    show,
    templates,
    currentComponentType,
    currentBuildingKey,
    removedTemplateIds,
    getTranslation,
    onSelectTemplate,
    onCreateNew,
    onClose
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    // The templates offered to this building: not archived, global or its own
    const availableTemplates = useMemo(() => {
        return templates.filter(template => templateOfferedTo(template, currentBuildingKey));
    }, [templates, currentBuildingKey]);

    // Filter by search term (case-insensitive)
    const filteredTemplates = useMemo(() => {
        if (!searchTerm.trim()) {
            return availableTemplates;
        }
        const term = searchTerm.toLowerCase();
        return availableTemplates.filter(template => {
            const name = template.name.toLowerCase();
            const typeName = getTranslation(template.primaryComponentType).toLowerCase();
            return name.includes(term) || typeName.includes(term);
        });
    }, [availableTemplates, searchTerm, getTranslation]);

    // Split into removed from this component, suggested (matching component type) and other templates
    const { removed, suggested, other } = useMemo(() => {
        const removed: MaintenanceTemplate[] = [];
        const suggested: MaintenanceTemplate[] = [];
        const other: MaintenanceTemplate[] = [];

        filteredTemplates.forEach(template => {
            if (removedTemplateIds?.includes(template.id)) {
                removed.push(template);
            } else if (template.primaryComponentType === currentComponentType) {
                suggested.push(template);
            } else {
                other.push(template);
            }
        });

        return { removed, suggested, other };
    }, [filteredTemplates, currentComponentType, removedTemplateIds]);

    if (!show) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <>
            <div
                className="modal show d-block"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                onMouseDown={handleBackdropClick}
            >
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <FileText size={20} className="me-2" />
                                Välj underhållsmall
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Stäng"
                            />
                        </div>
                        <div className="modal-body">
                            {/* Search input */}
                            <div className="input-group mb-3">
                                <span className="input-group-text">
                                    <Search size={18} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Sök mall..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    autoFocus
                                />
                                {searchTerm && (
                                    <button
                                        className="btn btn-outline-secondary"
                                        type="button"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        <X size={18} />
                                    </button>
                                )}
                            </div>

                            {/* Template lists */}
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {/* Removed from this component: picking one puts the row back */}
                                {removed.length > 0 && (
                                    <div className="mb-3">
                                        <h6 className="text-muted mb-2">Borttagna från komponenten</h6>
                                        <div className="list-group">
                                            {removed.map(template => (
                                                <button
                                                    key={template.id}
                                                    type="button"
                                                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                    onClick={() => onSelectTemplate(template)}
                                                >
                                                    <div>
                                                        <span className="fw-medium">{template.name}</span>
                                                        <span className="text-muted ms-2">
                                                            - {getTranslation(template.primaryComponentType)}
                                                        </span>
                                                    </div>
                                                    {template.buildingKey && (
                                                        <span className="badge bg-secondary">Fastighetsspecifik</span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Suggested templates */}
                                {suggested.length > 0 && (
                                    <div className="mb-3">
                                        <h6 className="text-muted mb-2">Föreslagna</h6>
                                        <div className="list-group">
                                            {suggested.map(template => (
                                                <button
                                                    key={template.id}
                                                    type="button"
                                                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                    onClick={() => onSelectTemplate(template)}
                                                >
                                                    <div>
                                                        <span className="fw-medium">{template.name}</span>
                                                        <span className="text-muted ms-2">
                                                            - {getTranslation(template.primaryComponentType)}
                                                        </span>
                                                    </div>
                                                    {template.buildingKey && (
                                                        <span className="badge bg-secondary">Fastighetsspecifik</span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Other templates */}
                                {other.length > 0 && (
                                    <div className="mb-3">
                                        <h6 className="text-muted mb-2">
                                            {suggested.length > 0 || removed.length > 0 ? 'Övriga mallar' : 'Alla mallar'}
                                        </h6>
                                        <div className="list-group">
                                            {other.map(template => (
                                                <button
                                                    key={template.id}
                                                    type="button"
                                                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                    onClick={() => onSelectTemplate(template)}
                                                >
                                                    <div>
                                                        <span className="fw-medium">{template.name}</span>
                                                        <span className="text-muted ms-2">
                                                            - {getTranslation(template.primaryComponentType)}
                                                        </span>
                                                    </div>
                                                    {template.buildingKey && (
                                                        <span className="badge bg-secondary">Fastighetsspecifik</span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* No results */}
                                {filteredTemplates.length === 0 && (
                                    <div className="text-center text-muted py-4">
                                        {searchTerm ? (
                                            <p>Inga mallar matchar "{searchTerm}"</p>
                                        ) : (
                                            <p>Inga mallar tillgängliga</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => onCreateNew(searchTerm.trim())}
                            >
                                <Plus size={16} className="me-2" />
                                Skapa ny mall
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                            >
                                Avbryt
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};
